/**
 * cus-info-variant.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles interactive behaviour for the cus_info variant swatch style:
 *
 *  1. SELECTED STATE — When a radio input changes, the associated
 *     .cus-op-info-swatch label gets/loses the `is-selected` class
 *     and its .checked-icon toggles the `f_hidden` class.
 *
 *  2. SAFETY BANNERS — Each .cus-val-safety element has a
 *     [data-safety-for] attribute containing the variant ID it belongs to.
 *     When the selection changes, only the matching banner is shown.
 *
 * Runs immediately as a module script (no DOMContentLoaded needed when
 * placed at the end of <body>, or use the event if placed in <head>).
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /**
   * Initialise all .cus-info-variant containers on the page.
   * Called once on load and again if new containers are injected
   * (e.g. quick-buy drawer).
   */
  function initCusInfoVariants() {
    document.querySelectorAll('.cus-info-variant').forEach(function (container) {
      if (container.dataset.cusInfoInit) return; // already set up
      container.dataset.cusInfoInit = '1';

      // The <fieldset> that wraps this container
      var fieldset = container.closest('fieldset');
      if (!fieldset) return;

      // Find all radio inputs that belong to this option group
      var radios = fieldset.querySelectorAll('input[type="radio"].sr-only');

      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          handleSelectionChange(fieldset, container, radio);
        });
      });

      // Sync initial state (page load / variant pre-selection)
      var checkedRadio = fieldset.querySelector('input[type="radio"].sr-only:checked');
      if (checkedRadio) {
        handleSelectionChange(fieldset, container, checkedRadio);
      }
    });
  }

  /**
   * Update visual state after a radio changes.
   *
   * @param {HTMLElement} fieldset   - The wrapping <fieldset>
   * @param {HTMLElement} container  - The .cus-info-variant div
   * @param {HTMLInputElement} radio - The newly-checked radio input
   */
  function handleSelectionChange(fieldset, container, radio) {
    var selectedId = radio.id;
    var selectedVariantId = radio.closest('fieldset')
      ? getVariantIdFromRadio(radio)
      : null;

    // ── 1. Swatch selected state ───────────────────────────────────────────
    container.querySelectorAll('.cus-op-info-swatch').forEach(function (swatch) {
      var forAttr = swatch.getAttribute('for');
      var isSelected = (forAttr === selectedId);

      swatch.classList.toggle('is-selected', isSelected);

      // Checkmark icon
      var icon = swatch.querySelector('.checked-icon');
      if (icon) {
        icon.classList.toggle('f_hidden', !isSelected);
      }
    });

    // ── 2. Safety banners ─────────────────────────────────────────────────
    // Banners live OUTSIDE the .cus-info-variant, inside .cus-val-safety-wrapper
    // which is a sibling of the container's parent <div>
    var safetyWrapper = fieldset.querySelector('.cus-val-safety-wrapper');
    if (!safetyWrapper) return;

    var banners = safetyWrapper.querySelectorAll('.cus-val-safety[data-safety-for]');
    if (!banners.length) return;

    // Figure out which variant ID to match
    // The safety banner uses [data-safety-for="{{ option_value.id }}"]
    var matchId = radio.value; // option_value.id (numeric string)

    banners.forEach(function (banner) {
      var bannerFor = banner.getAttribute('data-safety-for');
      var show = (bannerFor === matchId);
      banner.classList.toggle('f_hidden', !show);
    });
  }

  /**
   * Extract a variant id stored on the radio or its label.
   * (Not strictly needed for banner matching but kept for extensibility.)
   */
  function getVariantIdFromRadio(radio) {
    var label = document.querySelector('label[for="' + CSS.escape(radio.id) + '"]');
    if (label) {
      return label.dataset.variantId || null;
    }
    return null;
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCusInfoVariants);
  } else {
    initCusInfoVariants();
  }

  // Re-run when Shopify injects new sections (e.g. quick buy, dynamic checkout)
  document.addEventListener('shopify:section:load', initCusInfoVariants);

  // Expose for manual re-init if needed
  window.initCusInfoVariants = initCusInfoVariants;
})();